import React, { useEffect, useState } from 'react';
import { EeConfirmDialog } from 'pages/Scenarios/components/EeConfirmDialog';
import { Button } from '@material-ui/core';
import { ScenarioColumn } from './scenarioColumn';
import {
  CalculateFundScenarios,
  FundScenarioInvestmentScenariosAdd,
  fundScenarioInvestmentScenariosChange,
} from 'modules/graphql';
import { useMutation, useQuery } from '@apollo/client';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks/useStores';
import { toJS } from 'mobx';
import { FUND_INVESTMENT } from 'modules/fundInvestments/queries';
import { useSnackbar } from 'hooks/useSnackbar';

export const DetailPanel = observer((props: any) => {
  const { fundScenarioStore } = useStores();

  const [confirmIsOpen, setConfirmIsOpen] = useState(false);
  const [warningIsOpen, setWarningIsOpen] = useState(false);
  const [formValues, setFormValues] = useState<any>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [changedItems, setChangedItems] = useState<any[]>([]);

  const { addSnackbar } = useSnackbar();

  const { error: fundInvestmentError } = useQuery(FUND_INVESTMENT, {
    variables: { id: props.rowData.investmentId },
  });

  const [calculateFundScenarios, { error: calculationError }] = useMutation(
    CalculateFundScenarios,
  );
  const [
    changeFundScenarioInvestmentScenarios,
    { error: changeError },
  ] = useMutation(fundScenarioInvestmentScenariosChange);
  const [addFundScenarioInvestmentScenarios, { error: addError }] = useMutation(
    FundScenarioInvestmentScenariosAdd,
  );

  useEffect(() => {
    if (calculationError) {
      setIsCalculating(false);
      fundScenarioStore.setIsCalculated(true);
      addSnackbar('Failed to calculate');
    }
  }, [calculationError]);

  useEffect(() => {
    updateScenariosState();
  }, [fundScenarioStore.fundInvestments]);

  useEffect(() => {
    if (isCalculating && fundScenarioStore.isCalculated) {
      setIsCalculating(!fundScenarioStore.isCalculated);
    }
  }, [fundScenarioStore.isCalculated]);

  useEffect(() => {
    if (fundInvestmentError) {
      addSnackbar('Failed to get fund investment');
    }
  }, [fundInvestmentError]);

  function updateScenariosState() {
    const scenarios = getInvestmentScenarios();
    scenarios.forEach((s: any) => {
      s.irr *= 100;
      if (s.capTableScenario?.id) {
        s.dilution = null;
      } else {
        s.dilution *= 100;
      }
    });

    setFormValues(scenarios);
  }

  function getInvestmentScenarios() {
    const { rowData } = props;
    const investment = toJS(fundScenarioStore.fundInvestments).filter(
      (f: any) => f.id === rowData.investmentId,
    ) as any;
    if (investment) {
      return investment[0].scenarios?.filter(
        (s: any) => s.fundScenario?.fund?.id === props.rowData.fundId,
      );
    }
    return [];
  }

  async function onSubmit() {
    setIsCalculating(true);
    fundScenarioStore.setIsCalculated(false);
    const { rowData } = props;
    // Convert the form values in arrays that fit the input of Graphql mutation.
    const fundInvestmentScenarios = prepareScenariosList(formValues).filter(
      (f: any) => f,
    );

    // Prepare add and update lists.
    const addFundInvestmentScenarios = Object.values(fundInvestmentScenarios)
      .filter((f: any) => f.add)
      .map((f: any) => f.add);

    const updateFundInvestmentScenarios = Object.values(fundInvestmentScenarios)
      .filter((f: any) => f.update)
      .map((f: any) => f.update);

    try {
      if (
        addFundInvestmentScenarios.length === 0 &&
        updateFundInvestmentScenarios.length === 0
      ) {
        // Calculate specific fund investment
        await calculateFundScenarios({
          variables: {
            fundId: rowData.fundId,
            investmentId: rowData.investmentId,
          },
        });
      } else {
        await CreateUpdateInvestmentScenarios(
          addFundInvestmentScenarios,
          updateFundInvestmentScenarios,
          rowData.investmentId,
        );

        if (addError || changeError) {
          setIsCalculating(false);
          fundScenarioStore.setIsCalculated(true);
          addSnackbar('Failed to add/update investments scenarios');
          return;
        }
      }

      if (isDirty) {
        setIsDirty(false);
        setChangedItems([]);
      }
    } catch (err) {
      console.error('Failed to update fund investment scenario: ', err);
      fundScenarioStore.setIsCalculated(true);
      addSnackbar('Failed to add/update investments scenarios');
    }
  }

  function saveCalculate() {
    // @ts-ignore
    onSubmit().then();
  }

  async function CreateUpdateInvestmentScenarios(
    addFundInvestmentScenarios: any,
    updateFundInvestmentScenarios: any,
    investmentId: any,
  ) {
    try {
      if (updateFundInvestmentScenarios.length > 0) {
        await changeFundScenarioInvestmentScenarios({
          variables: {
            scenarios: updateFundInvestmentScenarios,
          },
        });
      }
      if (addFundInvestmentScenarios.length > 0) {
        await addFundScenarioInvestmentScenarios({
          variables: {
            scenarioSets: [
              {
                fundInvestmentId: investmentId,
                scenarios: addFundInvestmentScenarios,
              },
            ],
          },
        });
      }
    } catch (err) {
      console.error('Failed to save scenario investment: ', err);
    }
  }

  function prepareScenariosList(formFields: any) {
    const scenarios = getInvestmentScenarios();
    return formFields.map((fundScenarioColumn: any) => {
      const updatedItem = changedItems.find(
        (c: any) => c.fundScenario.id === fundScenarioColumn.fundScenario.id,
      );
      if (!updatedItem) return;

      let updatedInvestmentScenarios = null;
      let dilution =
        fundScenarioColumn.dilution === '' ? null : fundScenarioColumn.dilution;
      if (fundScenarioColumn.dilution) {
        if (typeof fundScenarioColumn.dilution === 'string') {
          dilution = fundScenarioColumn.dilution.replace(/,/g, '');
        }
        dilution = parseFloat(dilution);
        if (dilution > 0) {
          dilution /= 100;
        }
      }

      let fundScenariosColumn = {
        id: undefined,
        fundScenarioId: fundScenarioColumn.fundScenario.id,
        exitAmount: parseFloat(fundScenarioColumn.exitAmount),
        dilution: dilution,
        exitDate: fundScenarioColumn.exitDate,
        capTableScenarioId: fundScenarioColumn.capTableScenario?.id,
      };

      if (scenarios && scenarios.length > 0) {
        updatedInvestmentScenarios = scenarios.filter(
          (i: any) =>
            i.fundScenario.id === fundScenarioColumn.fundScenario.id &&
            i.id !== null,
        );

        if (
          updatedInvestmentScenarios &&
          updatedInvestmentScenarios.length > 0
        ) {
          fundScenariosColumn.id = updatedInvestmentScenarios[0].id;
          delete fundScenariosColumn.fundScenarioId;
          return { update: fundScenariosColumn };
        }
      }

      return { add: fundScenariosColumn };
    });
  }

  const disabled = props.rowData.status !== 'A_10';
  const fundInvestment = toJS(fundScenarioStore.fundInvestments).find(
    (f: any) => f.id === props.rowData.investmentId,
  ) as any;
  return (
    <div
      className="d-flex flex-column justify-content-between align-items-end"
      style={{ backgroundColor: '#f5f7f9' }}
    >
      <EeConfirmDialog
        open={confirmIsOpen}
        confirmButtonLabel="revert"
        onClose={() => setConfirmIsOpen(false)}
        onCancel={() => {
          setConfirmIsOpen(false);
        }}
        onConfirm={async () => {
          updateScenariosState();
          setChangedItems([]);
          setConfirmIsOpen(false);
        }}
      >
        <p className="fs-16 mb-0">Revert changes?</p>
        <p className="fs-14 mb-0">
          Do you want to revet all changes to the previous values?
        </p>
      </EeConfirmDialog>

      <EeConfirmDialog
        open={warningIsOpen}
        confirmButtonLabel="SAVE"
        cancelButtonLabel="OKAY"
        onClose={() => setWarningIsOpen(false)}
        onCancel={() => {
          setWarningIsOpen(false);
        }}
        onConfirm={async () => {
          saveCalculate();
          setWarningIsOpen(false);
        }}
      >
        <p className="fs-16 mb-0">
          Please save the new data for {props.rowData.company}.
        </p>
        <p className="fs-14 mb-0">
          If you leave now, you will loose new values you just input.
        </p>
      </EeConfirmDialog>

      <div className="d-flex justify-content-end py-5 px-3 z-index-0">
        {isDirty && (
          <Button
            onClick={() => setConfirmIsOpen(true)}
            className="mx-4"
            disabled={disabled || !fundScenarioStore.isCalculated}
            style={{
              height: 24,
              fontSize: 12,
              color: '#5d8bbe',
              fontWeight: 'bold',
            }}
          >
            revert
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={
            disabled || !fundScenarioStore.isCalculated || isCalculating
          }
          // todo(vmyshko): make global style for small buttons
          style={{ width: 152, height: 24, fontSize: 12, fontWeight: 'bold' }}
          onClick={() => {
            saveCalculate();
          }}
        >
          save & calculate
        </Button>
      </div>
      <div className="d-flex flex-row justify-content-center pb-5 align-self-center">
        {formValues?.map((s: any, i: number) => {
          return (
            <ScenarioColumn
              key={`sc-column${i}`}
              fundCurrencySign={props.fundCurrencySign}
              disabled={disabled}
              captableScenarios={fundInvestment.company.capTableScenarios}
              isLoading={isCalculating}
              onChange={(investmentItem: any, name: string, value: string) => {
                //make shallow copy
                let items = [...formValues];
                let item = {
                  ...items[investmentItem.fundScenario.position],
                };
                item[name] = value;
                if (name === 'capTableScenario') {
                  item.dilution = null;
                  item[name] = value === '' ? null : { id: value };
                } else if (
                  name === 'dilution' &&
                  (!!item.dilution || item.dilution === 0)
                ) {
                  item.capTableScenario = null;
                }
                items[item.fundScenario.position] = item;
                setFormValues(items);

                if (
                  !changedItems.find(
                    (i: any) =>
                      i.fundScenario.id === investmentItem.fundScenario.id,
                  )
                ) {
                  changedItems.push(investmentItem);
                  setChangedItems(changedItems);
                }
                setIsDirty(true);
              }}
              rowData={props.rowData}
              investmentScenario={s}
            />
          );
        })}
      </div>
    </div>
  );
});
